import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Inspection, LandlordTask } from '@vuokramani/shared';
import { api, ApiError } from './api.ts';
import { resizeImage } from './images.ts';

const BASE = import.meta.env.VITE_API_URL ?? '/api';

export function useInspections(leaseId: string | undefined) {
  return useQuery({
    queryKey: ['inspections', leaseId],
    queryFn: () =>
      api<{ inspections: Inspection[] }>(`/leases/${leaseId}/inspections`).then(
        (r) => r.inspections,
      ),
    enabled: Boolean(leaseId),
  });
}

export function photoUrl(id: string, download = false): string {
  return `${BASE}/inspection-photos/${id}/content${download ? '?download=1' : ''}`;
}

/** Kaikki tarkastuksen muutokset päivittävät saman kyselyn ja tehtävälistan. */
function useInspectionMutation<T>(leaseId: string, fn: (vars: T) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['inspections', leaseId] });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useInspectionActions(leaseId: string) {
  return {
    start: useInspectionMutation(leaseId, (type: 'move_in' | 'move_out') =>
      api(`/leases/${leaseId}/inspections`, { method: 'POST', body: { type } }),
    ),
    addRoom: useInspectionMutation(leaseId, ({ id, name }: { id: string; name: string }) =>
      api(`/inspections/${id}/rooms`, { method: 'POST', body: { name } }),
    ),
    updateRoom: useInspectionMutation(
      leaseId,
      ({ id, ...body }: { id: string; name?: string; note?: string | null }) =>
        api(`/inspection-rooms/${id}`, { method: 'PATCH', body }),
    ),
    deleteRoom: useInspectionMutation(leaseId, (id: string) =>
      api(`/inspection-rooms/${id}`, { method: 'DELETE' }),
    ),
    deletePhoto: useInspectionMutation(leaseId, (id: string) =>
      api(`/inspection-photos/${id}`, { method: 'DELETE' }),
    ),
    submit: useInspectionMutation(leaseId, (id: string) =>
      api(`/inspections/${id}/submit`, { method: 'POST' }),
    ),
    approve: useInspectionMutation(leaseId, (id: string) =>
      api(`/inspections/${id}/approve`, { method: 'POST' }),
    ),
    giveBack: useInspectionMutation(leaseId, ({ id, comment }: { id: string; comment: string }) =>
      api(`/inspections/${id}/return`, { method: 'POST', body: { comment } }),
    ),
    addNote: useInspectionMutation(leaseId, ({ id, text }: { id: string; text: string }) =>
      api(`/inspections/${id}/notes`, { method: 'POST', body: { text } }),
    ),
  };
}

/** Lähettää kuvat yksi kerrallaan, pienennettyinä. onProgress kertoo, monesko on menossa. */
export function useUploadPhotos(leaseId: string) {
  return useInspectionMutation(
    leaseId,
    async ({
      roomId,
      files,
      onProgress,
    }: {
      roomId: string;
      files: File[];
      onProgress?: (done: number, total: number) => void;
    }) => {
      for (const [i, file] of files.entries()) {
        onProgress?.(i, files.length);
        const blob = await resizeImage(file);
        const form = new FormData();
        form.append('capturedAt', String(file.lastModified || ''));
        const name = blob === file ? file.name : file.name.replace(/\.[^.]+$/, '') + '.jpg';
        form.append('file', blob, name);
        const response = await fetch(`${BASE}/inspection-rooms/${roomId}/photos`, {
          method: 'POST',
          credentials: 'include',
          body: form,
        });
        if (!response.ok) {
          const data = (await response.json().catch(() => null)) as { error?: string } | null;
          throw new ApiError(response.status, data?.error ?? 'server_error', file.name);
        }
      }
      onProgress?.(files.length, files.length);
    },
  );
}

export function useTasks(enabled: boolean) {
  return useQuery({
    queryKey: ['tasks'],
    queryFn: () => api<{ tasks: LandlordTask[] }>('/tasks').then((r) => r.tasks),
    enabled,
  });
}
